import Vue from 'vue';
import VueScrollTo from 'vue-scrollto';
import registerComponents from 'vue/globalcomponents';
import registerDirectives from 'vue/globaldirectives';
import promptManager from 'modules/common/components/MPrompt/MPromptManager';
import nextFrame from 'utils/nextFrame';
import tracker from 'tracking';
import setupTooltip from 'vue/setupTooltip';
import {showNotification} from 'components/common/MNotification/MNotificationManager';
import translation from 'translation';
import getRouteHref from 'router/utils/getRouteHref';
import VueCompositionApi from '@vue/composition-api';
import {VuelidatePlugin} from '@vuelidate/core';
import {isCurrentAppSeller} from 'utils/appTypeState';
import modalPlugin from 'modals/modalPlugin';

import '@marilyn/marilyn-ui/dist/main.css';
import '!file-loader?name=img/marilyn-ui.svg!@marilyn/marilyn-ui/dist/img/marilyn-ui.svg';
import MarilynUI from '@marilyn/marilyn-ui';
import store from 'store';
import sellerStore from 'store/indexSeller';

const currentLocale = () => {
  if (isCurrentAppSeller()) {
    return sellerStore.getters['sellerUser/userLocale'];
  }

  return store.getters['user/userLocale'];
};

const currentCalendarLocaleParams = () => {
  if (isCurrentAppSeller()) {
    return sellerStore.getters['sellerUser/calendarLocaleParams'];
  }

  return store.getters['params/calendarLocaleParams'];
};

Vue.use(VueCompositionApi);
Vue.use(VuelidatePlugin);
Vue.use(modalPlugin);

Vue.use(MarilynUI, {
  translations: {
    no_matches_found: () => translation.tr('No matches found'),
    you_cannot_select_this_item: () => translation.tr('You cannot select this item.'),
    go_to_page: () => translation.tr('Go to page'),
    show_per_page: () => translation.tr('Show per page'),
    show_x_to_y_of_z: () => translation.tr('Show {0} - {1} of {2}'),
    pagination_no_items: () => translation.trc('pagination', 'No items'),
    search: () => translation.tr('Search'),
    select_all: () => translation.tr('Select all'),
    default_settings: () => translation.tr('Default settings'),
    table_settings: () => translation.tr('Table settings'),
    apply: () => translation.tr('Apply'),
    cancel: () => translation.tr('Cancel'),
    selected_columns: () => translation.tr('Selected columns ({0})'),
    items_chosen_format: () => translation.tr('Items chosen: {0}'),
    no_table_presets_found: () => translation.tr('You have not created any templates yet'),
    save_as_preset: () => translation.tr('Save as preset'),
    edit_table_preset: () => translation.tr('Editing of preset'),
    start_date: () => translation.tr('Start date'),
    end_date: () => translation.tr('End date'),
    all_time: () => translation.tr('All time'),
    yesterday: () => translation.tr('Yesterday'),
    this_week: () => translation.tr('This week'),
    last_week: () => translation.tr('Last week'),
    this_month: () => translation.tr('This month'),
    last_month: () => translation.tr('Last month'),
    reset: () => translation.tr('Reset'),
    last_x_days: (days = 7) => Vue.prototype.ngettext('Last {0} day', 'Last {0} days', days).format(days),
    select: () => translation.tr('Select')
  },
  params: {
    locale: () => currentLocale(),
    calendarParams: () => currentCalendarLocaleParams()
  }
});

Vue.prototype.gettext = (text) => translation.tr(text).format({});
Vue.prototype._ = Vue.prototype.gettext;
Vue.prototype.pgettext = translation.trc.bind(translation);
Vue.prototype.ngettext = translation.ngettext.bind(translation);

Vue.prototype.url = getRouteHref;

Vue.prototype.$prompt = promptManager;

Vue.prototype.$notify = showNotification;

Vue.prototype.$nextFrame = nextFrame;

Vue.prototype.$nextFrameUntilDone = async function nextFrameUntilDone (callback, attempts = 100) {
  const waiter = () => {
    const done = callback();
    attempts -= 1;

    if (!done && attempts) {
      this.$nextFrame(waiter);
    }
  };

  return this.$nextFrame(waiter);
};

Vue.prototype.$tracker = tracker;

function _scrollToSelector (resolve, reject, element, animate, container, offset, force) {
  const selectorOffset = offset === false
    ? -element.offsetParent.offsetHeight / 2 + element.offsetHeight / 2
    : offset;
  const scrolledContainer = container ? container : element.offsetParent;

  const params = {
    container: scrolledContainer,
    easing: 'ease-in',
    duration: animate ? 500 : -1,
    offset: selectorOffset,
    force,
    cancelable: true,

    // these functions have no defaults in VueScrollTo
    onStart () {
    },
    onDone: resolve,
    onCancel: reject,
    x: false,
    y: true
  };
  VueScrollTo.scrollTo(element, params);
}

Vue.prototype.$scrollTo = async function scrollTo (selector, animate = true, attempts = 100, force = false, container = '', offset = false) {
  return new Promise((resolve, reject) => {
    this.$nextFrameUntilDone(() => {
      const $activeNode = document.querySelector(selector);
      if ($activeNode && $activeNode.offsetParent) {
        _scrollToSelector(resolve, reject, $activeNode, animate, container, offset, force);
        return true;
      }
      return false;
    }, attempts);
  });
};

// https://github.com/vuejs/vue/issues/844#issuecomment-390498696
Vue.prototype.$watchAll = function (props, watcher) {
  this.$watch(
    vm => props.map(prop => vm[prop]).concat([Date.now()]),
    watcher.bind(this)
  );
};

registerComponents(Vue);
registerDirectives(Vue);

setupTooltip();
